import React, { useEffect, useRef, useState, useCallback } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Skills from './components/Skills';
import Experience from './components/Experience';
import Projects from './components/Projects';
import Achievements from './components/Achievements';
import ResumePreview from './components/ResumePreview';
import Contact from './components/Contact';
import LoadingScreen from './components/LoadingScreen';
import RecruiterPanel from './components/RecruiterPanel';
import useScrollReveal from './hooks/useScrollReveal';
// import useCursor from './hooks/useCursor';


function applyTheme(dark) {
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

/* ── Space Background ── */
function SpaceBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    let stars = [], nebulae = [], suns = [], shooters = [];

    const init = () => {
      stars = []; nebulae = []; suns = [];
      for (let i = 0; i < 180; i++) {
        stars.push({ x: Math.random()*W, y: Math.random()*H, r: Math.random()*1.5+0.15, a: Math.random(), da: (Math.random()*0.004+0.001)*(Math.random()<0.5?1:-1), gold: Math.random()<0.1, bright: Math.random() });
      }
      nebulae.push({ x:W*0.2, y:H*0.3, rx:180, ry:90, a:0.03, rot:0.2, gold:true });
      nebulae.push({ x:W*0.8, y:H*0.7, rx:220, ry:110, a:0.02, rot:-0.4, gold:false });
      suns.push({ x:W*0.5, y:H*0.4, r:24, a:0.1, phase:0, speed:0.003 });
    };

    const handleResize = () => { W=canvas.width=window.innerWidth; H=canvas.height=window.innerHeight; init(); };
    window.addEventListener('resize', handleResize);
    init();

    let animId;
    const draw = () => {
      ctx.clearRect(0,0,W,H);
      const scrollY = window.scrollY||0;
      nebulae.forEach(n => {
        ctx.save(); const dy=(n.y-scrollY*0.15+H)%H;
        ctx.translate(n.x,dy); ctx.rotate(n.rot);
        const g=ctx.createRadialGradient(0,0,0,0,0,n.rx);
        if(n.gold){ g.addColorStop(0,`rgba(212,168,67,${n.a})`); g.addColorStop(0.4,`rgba(180,130,40,${n.a*0.4})`); g.addColorStop(1,'rgba(0,0,0,0)'); }
        else { g.addColorStop(0,`rgba(200,200,255,${n.a*0.6})`); g.addColorStop(0.4,`rgba(150,150,220,${n.a*0.25})`); g.addColorStop(1,'rgba(0,0,0,0)'); }
        ctx.scale(1,n.ry/n.rx); ctx.beginPath(); ctx.arc(0,0,n.rx,0,Math.PI*2); ctx.fillStyle=g; ctx.fill(); ctx.restore();
      });
      suns.forEach(s => {
        s.phase+=s.speed; const pulse=1+Math.sin(s.phase)*0.15;
        const dy=(s.y-scrollY*0.2+H)%H;
        const gr=ctx.createRadialGradient(s.x,dy,0,s.x,dy,s.r*4*pulse);
        gr.addColorStop(0,`rgba(240,200,80,${s.a})`); gr.addColorStop(0.3,`rgba(212,168,67,${s.a*0.5})`); gr.addColorStop(0.7,`rgba(180,120,30,${s.a*0.12})`); gr.addColorStop(1,'rgba(0,0,0,0)');
        ctx.beginPath(); ctx.arc(s.x,dy,s.r*4*pulse,0,Math.PI*2); ctx.fillStyle=gr; ctx.fill();
        ctx.beginPath(); ctx.arc(s.x,dy,s.r*0.4*pulse,0,Math.PI*2); ctx.fillStyle=`rgba(255,240,180,${s.a*1.5})`; ctx.fill();
      });
      stars.forEach(s => {
        s.a+=s.da; if(s.a>1||s.a<0.04) s.da*=-1;
        const factor=s.gold?0.05:0.08;
        const dy=(s.y-scrollY*factor+H)%H;
        ctx.beginPath(); ctx.arc(s.x,dy,s.r,0,Math.PI*2);
        ctx.fillStyle=s.gold?`rgba(212,168,67,${s.a*0.75})`:`rgba(230,230,255,${s.a*0.8})`; ctx.fill();
        if(s.bright>0.85&&s.r>1){ ctx.beginPath(); ctx.arc(s.x,dy,s.r*3,0,Math.PI*2); ctx.fillStyle=s.gold?`rgba(212,168,67,${s.a*0.07})`:`rgba(200,200,255,${s.a*0.05})`; ctx.fill(); }
      });
      shooters=shooters.filter(s=>{
        s.x+=s.vx; s.y+=s.vy; s.a-=0.014;
        if(s.a<=0) return false;
        const tl=s.len, g=ctx.createLinearGradient(s.x-s.vx*(tl/s.vx),s.y-s.vy*(tl/s.vx),s.x,s.y);
        g.addColorStop(0,'rgba(212,168,67,0)'); g.addColorStop(0.55,`rgba(235,210,140,${s.a*0.55})`); g.addColorStop(1,`rgba(255,248,220,${s.a})`);
        ctx.beginPath(); ctx.moveTo(s.x-s.vx*(tl/s.vx),s.y-s.vy*(tl/s.vx)); ctx.lineTo(s.x,s.y);
        ctx.strokeStyle=g; ctx.lineWidth=1.6; ctx.stroke();
        ctx.beginPath(); ctx.arc(s.x,s.y,2,0,Math.PI*2); ctx.fillStyle=`rgba(255,248,200,${s.a*0.8})`; ctx.fill();
        return true;
      });
      animId=requestAnimationFrame(draw);
    };

    const spawnInt=setInterval(()=>{ if(shooters.length<3){ const angle=(Math.random()*60+20)*Math.PI/180,speed=3.5+Math.random()*4.5; shooters.push({x:Math.random()*W,y:Math.random()*H*0.4,vx:Math.cos(angle)*speed,vy:Math.sin(angle)*speed,len:60+Math.random()*80,a:1}); } },4000);
    draw();
    return ()=>{ window.removeEventListener('resize',handleResize); cancelAnimationFrame(animId); clearInterval(spawnInt); };
  }, []);
  return <canvas ref={canvasRef} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none',width:'100vw',height:'100vh',background:'#05070c'}} />;
}

/* ── Sparkles ── */
function SparklesBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas=canvasRef.current; if(!canvas) return;
    const ctx=canvas.getContext('2d');
    let w=canvas.width=window.innerWidth, h=canvas.height=window.innerHeight;
    const sparkles=[]; const count=15;
    const mk=(initY=false)=>({ x:Math.random()*w, y:initY?Math.random()*h:h+10, vx:(Math.random()-0.5)*0.35, vy:-(Math.random()*0.4+0.15), r:Math.random()*1.4+0.6, alpha:0, maxAlpha:Math.random()*0.6+0.2, fadeSpeed:0.003+Math.random()*0.005, fadingIn:true, phase:Math.random()*Math.PI*2, phaseSpeed:0.01+Math.random()*0.02, amplitude:0.2+Math.random()*0.5 });
    for(let i=0;i<count;i++) sparkles.push(mk(true));
    const resize=()=>{ w=canvas.width=window.innerWidth; h=canvas.height=window.innerHeight; };
    window.addEventListener('resize',resize);
    let animId;
    const animate=()=>{
      ctx.clearRect(0,0,w,h);
      sparkles.forEach((s,idx)=>{
        s.phase+=s.phaseSpeed; s.x+=s.vx+Math.sin(s.phase)*s.amplitude*0.1; s.y+=s.vy;
        if(s.fadingIn){ s.alpha+=s.fadeSpeed; if(s.alpha>=s.maxAlpha) s.fadingIn=false; } else s.alpha-=s.fadeSpeed;
        if(s.alpha>0){
          const g=ctx.createRadialGradient(s.x,s.y,0,s.x,s.y,s.r*4);
          g.addColorStop(0,`rgba(255,230,150,${s.alpha})`); g.addColorStop(0.3,`rgba(212,168,67,${s.alpha*0.5})`); g.addColorStop(1,'rgba(0,0,0,0)');
          ctx.fillStyle=g; ctx.beginPath(); ctx.arc(s.x,s.y,s.r*4,0,Math.PI*2); ctx.fill();
          ctx.fillStyle=`rgba(255,255,255,${s.alpha*0.8})`; ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2); ctx.fill();
        }
        if(s.y<-10||s.x<-10||s.x>w+10||(s.alpha<=0&&!s.fadingIn)) sparkles[idx]=mk(false);
      });
      animId=requestAnimationFrame(animate);
    };
    animate();
    return ()=>{ window.removeEventListener('resize',resize); cancelAnimationFrame(animId); };
  },[]);
  return <canvas ref={canvasRef} style={{position:'fixed',inset:0,zIndex:100,pointerEvents:'none',width:'100vw',height:'100vh'}} />;
}

/* ── Custom Cursor ── */
function CustomCursor() {
  const dotRef=useRef(null), ringRef=useRef(null), pos=useRef({x:0,y:0}), ring=useRef({x:0,y:0});
  useEffect(()=>{
    if(window.matchMedia('(max-width:768px)').matches) return;
    const dot=dotRef.current, ringEl=ringRef.current;
    const move=e=>{ pos.current={x:e.clientX,y:e.clientY}; if(dot) dot.style.transform=`translate3d(${e.clientX}px,${e.clientY}px,0)`; };
    document.addEventListener('mousemove',move);
    let raf;
    const animate=()=>{ ring.current.x+=(pos.current.x-ring.current.x)*0.12; ring.current.y+=(pos.current.y-ring.current.y)*0.12; if(ringEl) ringEl.style.transform=`translate3d(${ring.current.x}px,${ring.current.y}px,0) translate(-50%,-50%)`; raf=requestAnimationFrame(animate); };
    animate();
    const grow=()=>document.body.classList.add('hov');
    const shrink=()=>document.body.classList.remove('hov');
    const add=()=>document.querySelectorAll('a,button,.li,.btn-g,.cccard,.nav-resume-btn,.light-button').forEach(el=>{ el.removeEventListener('mouseenter',grow); el.removeEventListener('mouseleave',shrink); el.addEventListener('mouseenter',grow); el.addEventListener('mouseleave',shrink); });
    const t=setInterval(add,1000); add();
    return ()=>{ document.removeEventListener('mousemove',move); cancelAnimationFrame(raf); clearInterval(t); };
  },[]);
  return <><div ref={dotRef} className="cursor-dot"/><div ref={ringRef} className="cursor-ring"/></>;
}

/* ── Scroll Progress ── */
function ScrollProgress() {
  const barRef=useRef(null);
  useEffect(()=>{
    const fn=()=>{ const p=window.scrollY/(document.body.scrollHeight-window.innerHeight)*100; if(barRef.current) barRef.current.style.width=p+'%'; };
    window.addEventListener('scroll',fn);
    return ()=>window.removeEventListener('scroll',fn);
  },[]);
  return <div ref={barRef} style={{position:'fixed',top:0,left:0,height:2,background:'linear-gradient(to right,var(--accent-color),var(--accent2-color))',zIndex:99999,width:0,transition:'width 0.08s linear'}} />;
}

/* ── Planetary Indicator ── */
function PlanetaryIndicator() {
  const earthRef=useRef(null), moonRef=useRef(null);
  useEffect(()=>{
    const fn=()=>{ const y=window.scrollY||0; if(earthRef.current) earthRef.current.style.transform=`rotate(${y*0.35}deg)`; if(moonRef.current) moonRef.current.style.transform=`rotate(${y*1.4}deg)`; };
    window.addEventListener('scroll',fn,{passive:true});
    return ()=>window.removeEventListener('scroll',fn);
  },[]);
  return (
    <div id="planetary-indicator">
      <div className="planet-sun"/>
      <div ref={earthRef} className="planet-earth-orbit"><div className="planet-earth"><div ref={moonRef} className="planet-moon-orbit"><div className="planet-moon"/></div></div></div>
    </div>
  );
}

/* ── Theme Toggle ── */
function ThemeToggle({ dark, onToggle }) {
  return (
    <button onClick={onToggle} title={dark?'Standard view':'Dark space protocol'}
      style={{
        position:'fixed', bottom:'2rem', right:'2rem', zIndex:500,
        width:44, height:44, borderRadius:3,
        background: dark ? '#0a0d16' : '#fff',
        border:`1px solid ${dark?'rgba(212,168,67,0.3)':'rgba(10,10,10,0.12)'}`,
        boxShadow: dark?'0 4px 20px rgba(212,168,67,0.12)':'0 2px 12px rgba(0,0,0,0.08)',
        cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
        transition:'all 0.25s',
      }}
      onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.08)'; e.currentTarget.style.borderColor='var(--accent-color)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.borderColor=dark?'rgba(212,168,67,0.3)':'rgba(10,10,10,0.12)';}}>
      {dark
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="1.8" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
      }
    </button>
  );
}

export default function App() {
  const [dark, setDark] = useState(()=> sessionStorage.getItem('vg-theme')==='dark');
  const [loadingTheme, setLoadingTheme] = useState(dark?'dark':'light');
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  useScrollReveal();
  useEffect(()=>{ applyTheme(dark); },[dark]);

  const toggleTheme = () => {
    const next=!dark;
    sessionStorage.setItem('vg-theme', next?'dark':'light');
    setLoadingTheme(next?'dark':'light');
    setLoading(true);
    setDark(next);
  };

  const handleLoadDone = useCallback(()=>{
    setLoading(false);
    document.body.style.overflow='';
    document.body.style.height='';
    document.documentElement.style.overflow='';
  },[]);

  return (
    <>
      {loading && <LoadingScreen onDone={handleLoadDone} dark={loadingTheme==='dark'} />}
      {dark && <SpaceBackground />}
      {dark && <SparklesBackground />}
      <CustomCursor />
      <ScrollProgress />
      {dark && <PlanetaryIndicator />}

      <RecruiterPanel isOpen={panelOpen} onClose={()=>setPanelOpen(false)} dark={dark} onToggleTheme={toggleTheme} />
      <ThemeToggle dark={dark} onToggle={toggleTheme} />
      <Navbar dark={dark} onOpenRecruiter={()=>setPanelOpen(true)} />

      {/* SECTION ORDER: Hero → About → Projects → Experience → Skills → Achievements → Resume → Contact */}
      <main style={{opacity:loading?0:1, transition:'opacity 0.4s ease 0.1s', overflow:'visible', position:'relative', zIndex:2}}>
        <Hero onOpenRecruiter={()=>setPanelOpen(true)} />
        <About />
        <Projects />
        <Experience />
<Skills  />
        <Achievements />
        <ResumePreview />
        <Contact />
      </main>

      <footer style={{
        position:'relative', zIndex:2,
        padding:'40px 60px',
        borderTop:'1px solid var(--border-color)',
        background:'transparent',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:'1rem',
      }}>
        <div>
          <div style={{fontFamily:'var(--bebas)', fontSize:18, letterSpacing:'3px', color:'var(--accent-color)', marginBottom:4}}>VIKASH GAUTAM</div>
          <div style={{fontFamily:'var(--mono)', fontSize:8, letterSpacing:'1.5px', color:'var(--text-muted)', textTransform:'uppercase'}}>Full Stack Developer · Open Source Founder · Java · React · Node.js · AI/ML</div>        </div>
        <div style={{display:'flex', gap:'2rem', flexWrap:'wrap'}}>
{[
  { label:'GitHub',   href:'https://github.com/vikash1311',
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22"/></svg> },
  { label:'LinkedIn', href:'https://linkedin.com/in/vikash2808',
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg> },
  { label:'Resume',   href:'https://drive.google.com/file/d/1RWy79TIwoKfm3U9b2SINZj3WifU7uWYz/view',
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
  { label:'Mail',     href:'mailto:gautam7.ven@gmail.com',
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg> },
].map(l=>(
  <a key={l.label} href={l.href} target="_blank" rel="noreferrer"
    style={{display:'flex', alignItems:'center', gap:5, fontFamily:'var(--mono)', fontSize:8.5, letterSpacing:'1.5px', color:'var(--text-muted)', textDecoration:'none', textTransform:'uppercase', transition:'color 0.2s'}}
    onMouseEnter={e=>e.currentTarget.style.color='var(--accent-color)'}
    onMouseLeave={e=>e.currentTarget.style.color='var(--text-muted)'}>
    {l.icon}{l.label}
  </a>
))}
        </div>
        <div style={{fontFamily:'var(--mono)', fontSize:8, letterSpacing:'1px', color:'var(--text-muted)'}}>
          © 2026 · Nagpur, India
        </div>
        <style>{`
          @media(max-width:640px){
            footer { padding: 28px 20px !important; flex-direction: column !important; gap: 1.2rem !important; }
          }
        `}</style>
      </footer>
    </>
  );
}